import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { cn } from 'lib/utils';
import {
  useGenerationFields,
  GENERATION_LANGUAGES,
  defaultGenerationLanguage,
} from 'modules/generationFields';
import {
  useMaxQuestionsPerGeneration,
  useOpenUpgradeForLimit,
  MAX_QUESTIONS_PER_GENERATION,
  FEATURES,
} from 'modules/entitlements';
import { UpgradeFieldBadge } from 'components/entitlements';
import { FieldPromptInput } from 'components/questionBank/FieldPromptInput';

// Default requested count, clamped to the plan cap when the dialog opens.
const DEFAULT_QUESTION_COUNT = 8;

export interface QuizGenConfig {
  questionCount: number;
  difficulty: 'easy' | 'medium' | 'hard';
  language: string;
  fieldKey: string;
  fieldPrompt: string;
}

interface QuizGenerateDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (config: QuizGenConfig) => void;
  isGenerating: boolean;
}

const DIFFICULTIES: Array<{ value: QuizGenConfig['difficulty']; labelKey: string }> = [
  { value: 'easy', labelKey: 'easy' },
  { value: 'medium', labelKey: 'medium' },
  { value: 'hard', labelKey: 'hard' },
];

/** Mirrors the generate-to-bank modal's inputs (count / difficulty / language / field steering). */
export const QuizGenerateDialog: React.FC<QuizGenerateDialogProps> = ({
  open,
  onClose,
  onGenerate,
  isGenerating,
}) => {
  const { t, i18n } = useTranslation();
  const { fields } = useGenerationFields();

  // Per-plan cap on questions per generation (backend enforces; this is UX).
  const maxQuestions = useMaxQuestionsPerGeneration();
  const openUpgrade = useOpenUpgradeForLimit();
  const isRestricted = maxQuestions < MAX_QUESTIONS_PER_GENERATION;

  const [questionCount, setQuestionCount] = useState(Math.min(DEFAULT_QUESTION_COUNT, maxQuestions));
  const [difficulty, setDifficulty] = useState<QuizGenConfig['difficulty']>('medium');
  const [language, setLanguage] = useState(() => defaultGenerationLanguage(i18n.language));
  const [fieldKey, setFieldKey] = useState('');
  const [fieldPrompt, setFieldPrompt] = useState('');

  // Reset to sensible defaults each time the dialog opens, clamped to the cap.
  useEffect(() => {
    if (open) {
      setQuestionCount(Math.min(DEFAULT_QUESTION_COUNT, maxQuestions));
      setDifficulty('medium');
      setLanguage(defaultGenerationLanguage(i18n.language));
      setFieldKey('');
      setFieldPrompt('');
    }
  }, [open, i18n.language, maxQuestions]);

  const overCap = questionCount > maxQuestions;

  const handleGenerate = () => {
    // Over the plan cap → upsell instead of a doomed request (which would show
    // an error toast and the 403-triggered dialog at the same time).
    if (overCap) {
      openUpgrade(FEATURES.AI_QUESTIONS_PER_GENERATION, maxQuestions, questionCount);
      return;
    }
    onGenerate({
      questionCount: Math.min(MAX_QUESTIONS_PER_GENERATION, Math.max(1, questionCount)),
      difficulty,
      language,
      fieldKey,
      fieldPrompt,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* No description to point at — tell Radix explicitly, or it warns. */}
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('aigen.generate_quiz')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="text-sm font-medium">{t('number_of_questions')}</label>
                {isRestricted && (
                  <UpgradeFieldBadge
                    featureKey={FEATURES.AI_QUESTIONS_PER_GENERATION}
                    limit={maxQuestions}
                    requested={questionCount}
                    tooltip={t('max_questions_plan_hint', { count: maxQuestions })}
                  />
                )}
              </div>
              <Input
                type="number"
                min={1}
                max={MAX_QUESTIONS_PER_GENERATION}
                value={questionCount}
                onChange={(e) =>
                  setQuestionCount(
                    Math.min(MAX_QUESTIONS_PER_GENERATION, Math.max(1, Number(e.target.value) || 1)),
                  )
                }
                className={cn(overCap && 'border-amber-400 focus-visible:ring-amber-400/50')}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('difficulty')}</label>
              <Select value={difficulty} onValueChange={(v) => setDifficulty(v as QuizGenConfig['difficulty'])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTIES.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {t(d.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">{t('language')}</label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENERATION_LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <FieldPromptInput
            fields={fields}
            fieldKey={fieldKey}
            fieldPrompt={fieldPrompt}
            onFieldKeyChange={setFieldKey}
            onFieldPromptChange={setFieldPrompt}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            {t('cancel')}
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating} className="gap-1.5">
            {isGenerating && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('generate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuizGenerateDialog;
